import React, { useEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Icon } from "semantic-ui-react";
import { shallowEqual, useSelector } from "react-redux";
import QuestionDistributionChart from "../../app/audit/simple/question/QuestionDistributionChart";

const DISTRIBUTION_SEGMENTS = [
  { key: "Low", label: "Low", color: "#F15062" },
  { key: "Neutral", label: "Neutral", color: "#B3B3B3" },
  { key: "High", label: "High", color: "#1D9A7D" },
];

const SCORE_BANDS = {
  Low: { min: 0, max: 4 },
  Neutral: { min: 5, max: 6 },
  High: { min: 7, max: 10 },
};

const EPSILON = 0.0001;

const SEGMENT_META = DISTRIBUTION_SEGMENTS.reduce((accumulator, segment) => {
  accumulator[segment.key] = segment;
  return accumulator;
}, {});

const formatNumber = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  const rounded = Math.round(value * 100) / 100;
  if (Number.isInteger(rounded)) {
    return String(rounded);
  }
  return rounded.toFixed(2).replace(/\.?0+$/, "");
};

const formatRange = (start, end) => `${formatNumber(start)}–${formatNumber(end)}`;

const categorizeScore = (rawScore) => {
  const score = parseFloat(rawScore);
  if (!Number.isFinite(score)) return null;
  if (score <= SCORE_BANDS.Low.max + EPSILON) return "Low";
  if (score <= SCORE_BANDS.Neutral.max + EPSILON) return "Neutral";
  if (score <= SCORE_BANDS.High.max + EPSILON) return "High";
  if (score < SCORE_BANDS.Low.min) return "Low";
  if (score > SCORE_BANDS.High.max) return "High";
  return null;
};

const buildBandDistribution = (question) => {
  if (!question || question.type !== "quantitative") return null;
  const distribution = question.responses?.distribution || {};

  const counts = { Low: 0, Neutral: 0, High: 0 };
  let countedTotal = 0;

  Object.entries(distribution).forEach(([scoreKey, countValue]) => {
    const count = Number(countValue) || 0;
    if (count <= 0) return;
    const band = categorizeScore(scoreKey);
    if (!band) return;
    counts[band] += count;
    countedTotal += count;
  });

  if (!countedTotal) return null;

  const segments = DISTRIBUTION_SEGMENTS.reduce((accumulator, segment) => {
    const count = counts[segment.key] || 0;
    if (!count) return accumulator;
    const percentage = (count / countedTotal) * 100;
    accumulator.push({
      name: segment.label,
      key: segment.key,
      data: [Number(percentage.toFixed(1))],
      color: segment.color,
      count,
    });
    return accumulator;
  }, []);

  const ranges = {
    Low: { start: SCORE_BANDS.Low.min, end: SCORE_BANDS.Low.max },
    Neutral: { start: SCORE_BANDS.Neutral.min, end: SCORE_BANDS.Neutral.max },
    High: { start: SCORE_BANDS.High.min, end: SCORE_BANDS.High.max },
  };

  return { segments, ranges };
};

const buildSegmentsForScores = (scores) => {
  const counts = { Low: 0, Neutral: 0, High: 0 };

  (scores || []).forEach((value) => {
    const band = categorizeScore(value);
    if (!band) return;
    counts[band] += 1;
  });

  const total = counts.Low + counts.Neutral + counts.High;

  return DISTRIBUTION_SEGMENTS.reduce((accumulator, segment) => {
    const count = counts[segment.key] || 0;
    if (!count || !total) return accumulator;
    const percentage = (count / total) * 100;
    accumulator.push({
      name: segment.label,
      key: segment.key,
      data: [Number(percentage.toFixed(1))],
      color: segment.color,
      count,
    });
    return accumulator;
  }, []);
};

const buildOpenEndedResponses = (question, respondents, isAnonymous) => {
  if (!question || question.type !== "openended") return [];

  return (respondents || [])
    .map((respondent) => {
      const responseData = respondent.responses?.[question.id];
      const response =
        typeof responseData === "string"
          ? responseData
          : responseData?.response;
      const followUp =
        typeof responseData === "object" ? responseData?.followUp : null;

      if (!response || !response.toString().trim()) {
        return null;
      }

      return {
        name: isAnonymous ? "Anonymous Respondent" : respondent.name,
        response,
        followUp,
      };
    })
    .filter(Boolean);
};

const MINIMUM_RESPONSE_THRESHOLD = 3;

const PulseAnalytics = ({
  pulseData = null,
  isAnonymous = false,
  orgRule: _orgRule = null,
  surveyInfo: _surveyInfo = {},
  onModalOpen,
}) => {
  const [activePrimaryTab, setActivePrimaryTab] = useState("scores");
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [selectedParentCategory, setSelectedParentCategory] = useState(null);
  const [selectedOpenEndedId, setSelectedOpenEndedId] = useState(null);

  const { get_employee_org_category } = useSelector(
    (state) => ({
      get_employee_org_category: state.employee_category.employee_org_category,
    }),
    shallowEqual
  );

  const { get_survey_structure, core_data } = useSelector(
    (state) => ({
      get_survey_structure: state.surveystructure?.survey_structure,
      core_data: state.audit?.core_data,
    }),
    shallowEqual
  );

  const getUserCategory = (employeeId) => {
    if (!get_employee_org_category || get_employee_org_category.length === 0) {
      return "Unknown Category";
    }

    const user = get_employee_org_category.find(
      (candidate) =>
        candidate.employee_id === employeeId ||
        candidate.employee === employeeId
    );

    const category = user?.category?.find(
      (cat) => cat?.priority === "secondary"
    )?.value;

    return category || "Unknown Category";
  };

  const dataSource = useMemo(() => {
    if (!pulseData) return pulseData;

    return {
      ...pulseData,
      respondents: pulseData.respondents?.map((respondent) => ({
        ...respondent,
        department: getUserCategory(respondent.id),
      })),
    };
  }, [pulseData, get_employee_org_category]);

  const hasRegisteredModalOpen = useRef(false);

  useEffect(() => {
    if (hasRegisteredModalOpen.current) return;
    if (!onModalOpen || typeof onModalOpen !== "function") return;

    onModalOpen(() => {});
    hasRegisteredModalOpen.current = true;
  }, [onModalOpen]);

  const quantitativeSummaries = useMemo(() => {
    return (dataSource?.questions || [])
      .filter((question) => question.type === "quantitative")
      .map((question) => {
        const bandData = buildBandDistribution(question);
        if (!bandData) return null;
        const { segments, ranges } = bandData;
        return {
          question,
          bands: segments,
          ranges,
          total: segments.reduce(
            (sum, item) => sum + (item.count || 0),
            0
          ),
        };
      })
      .filter((summary) => summary && summary.total >= MINIMUM_RESPONSE_THRESHOLD);
  }, [dataSource]);

  const openEndedQuestionOptions = useMemo(
    () =>
      (dataSource?.questions || [])
        .filter((question) => question.type === "openended")
        .map((question) => ({
          question,
          responses: buildOpenEndedResponses(
            question,
            dataSource?.respondents,
            isAnonymous
          ),
        }))
        .filter(
          (option) => option.responses.length >= MINIMUM_RESPONSE_THRESHOLD
        ),
    [dataSource, isAnonymous]
  );

  useEffect(() => {
    if (
      activePrimaryTab === "scores" &&
      quantitativeSummaries.length === 0 &&
      openEndedQuestionOptions.length > 0
    ) {
      setActivePrimaryTab("openEnded");
    }
  }, [activePrimaryTab, quantitativeSummaries, openEndedQuestionOptions]);

  useEffect(() => {
    if (!quantitativeSummaries.length) {
      if (selectedQuestionId !== null) {
        setSelectedQuestionId(null);
      }
      return;
    }

    if (!selectedQuestionId) return;

    const stillExists = quantitativeSummaries.some(
      (summary) => summary.question.id === selectedQuestionId
    );

    if (!stillExists) {
      setSelectedQuestionId(null);
    }
  }, [quantitativeSummaries, selectedQuestionId]);

  useEffect(() => {
    if (!openEndedQuestionOptions.length) {
      setSelectedOpenEndedId(null);
      return;
    }

    if (!selectedOpenEndedId) {
      setSelectedOpenEndedId(openEndedQuestionOptions[0].question.id);
      return;
    }

    const stillExists = openEndedQuestionOptions.some(
      (option) => option.question.id === selectedOpenEndedId
    );

    if (!stillExists) {
      setSelectedOpenEndedId(openEndedQuestionOptions[0].question.id);
    }
  }, [openEndedQuestionOptions, selectedOpenEndedId]);

  const visibleCategories = useMemo(() => {
    const categories =
      core_data?.categories?.[0]?.categories ||
      get_survey_structure?.[0]?.categories;
    if (!categories) return [];
    return categories.filter((category) => category?.visibility !== "private");
  }, [core_data, get_survey_structure]);

  const parentCategoryOptions = useMemo(() => {
    return (visibleCategories || [])
      .filter((category) => category?.priority !== "primary")
      .map((category) => ({
        key: category.name,
        name: category.name,
        priority: category.priority,
      }));
  }, [visibleCategories]);

  useEffect(() => {
    const validKeys = new Set(
      (parentCategoryOptions || []).map((option) => option.key)
    );

    if (!validKeys.size) {
      setSelectedParentCategory(null);
      return;
    }

    if (!selectedParentCategory || !validKeys.has(selectedParentCategory)) {
      const preferred =
        parentCategoryOptions.find(
          (option) => option.priority === "secondary"
        ) || parentCategoryOptions[0];
      setSelectedParentCategory(preferred.key);
    }
  }, [parentCategoryOptions, selectedParentCategory]);

  const selectedQuestionSummary = useMemo(
    () =>
      quantitativeSummaries.find(
        (summary) => summary.question.id === selectedQuestionId
      ) || null,
    [quantitativeSummaries, selectedQuestionId]
  );

  const selectedQuestion = selectedQuestionSummary?.question || null;
  const sharedRangeInfo = useMemo(() => {
    if (!quantitativeSummaries.length) return null;
    const firstRanges = quantitativeSummaries[0].ranges;
    if (!firstRanges) return null;

    const consistent = quantitativeSummaries.every((summary) =>
      summary.ranges &&
      DISTRIBUTION_SEGMENTS.every(({ key }) => {
        const candidate = summary.ranges[key];
        const baseline = firstRanges[key];
        if (!candidate || !baseline) return false;
        return (
          Math.abs(candidate.start - baseline.start) < 0.0001 &&
          Math.abs(candidate.end - baseline.end) < 0.0001
        );
      })
    );

    return { ranges: firstRanges, consistent };
  }, [quantitativeSummaries]);

  const categoryDistributions = useMemo(() => {
    if (
      !selectedQuestion ||
      selectedQuestion.type !== "quantitative" ||
      !selectedParentCategory
    ) {
      return [];
    }

    const parent = (visibleCategories || []).find(
      (category) => category?.name === selectedParentCategory
    );
    if (!parent) return [];

    const children = [];
    (parent.options || []).forEach((option) => {
      if (option?.id) {
        children.push({ id: option.id, name: option.name });
      } else if (option?.name) {
        children.push({ name: option.name });
      }
      (option?.subfactors || []).forEach((subfactor) => {
        if (subfactor?.id) {
          children.push({ id: subfactor.id, name: subfactor.name });
        }
      });
    });

    if (!children.length) return [];

    const employeeMembership = new Map();
    (get_employee_org_category || []).forEach((member) => {
      const memberships = member.category || [];
      let match = null;
      if (parent.priority === "primary" || parent.priority === "secondary") {
        match = memberships.find(
          (category) => category?.priority === parent.priority
        );
      } else {
        match = memberships.find((category) => category?.name === parent.name);
      }
      if (!match) return;
      const identifier = match.value_id || match.value || match.name;
      if (!identifier) return;
      employeeMembership.set(
        member.employee_id || member.employee,
        identifier
      );
    });

    const scoresByChild = new Map();
    children.forEach((child) => {
      const key = child.id || child.name;
      if (key) {
        scoresByChild.set(key, []);
      }
    });

    (dataSource?.respondents || []).forEach((respondent) => {
      const responseRecord =
        respondent.responses?.[selectedQuestion.id]?.response ??
        respondent.responses?.[selectedQuestion.id];
      const score = parseFloat(responseRecord);
      if (!Number.isFinite(score)) return;

      const membershipKey = employeeMembership.get(respondent.id);
      if (!membershipKey || !scoresByChild.has(membershipKey)) return;

      scoresByChild.get(membershipKey).push(score);
    });

    return children
      .map((child) => {
        const key = child.id || child.name;
        if (!key) return null;
        const scores = scoresByChild.get(key) || [];
        const segments = buildSegmentsForScores(scores);
        const total = scores.length;
        const average =
          scores.length > 0
            ? scores.reduce((sum, value) => sum + value, 0) / scores.length
            : null;
        return {
          name: child.name,
          segments,
          total,
          average,
        };
      })
      .filter(Boolean)
      .filter((item) => item.total >= MINIMUM_RESPONSE_THRESHOLD);
  }, [
    selectedQuestion,
    selectedParentCategory,
    visibleCategories,
    get_employee_org_category,
    dataSource,
  ]);

  const selectedOpenEndedOption = useMemo(
    () =>
      openEndedQuestionOptions.find(
        (option) => option.question.id === selectedOpenEndedId
      ) || null,
    [openEndedQuestionOptions, selectedOpenEndedId]
  );

  const selectedOpenEndedQuestion = selectedOpenEndedOption?.question || null;
  const openEndedResponses = selectedOpenEndedOption?.responses || [];

  if (!pulseData) {
    return (
      <Container>
        <LoadingState>
          <LoadingSpinner />
          <div>Loading analytics…</div>
        </LoadingState>
      </Container>
    );
  }

  const totalRespondents = dataSource?.respondents?.length || 0;

  if (totalRespondents < MINIMUM_RESPONSE_THRESHOLD) {
    return (
      <Container>
        <ComparisonPlaceholder>
          At least {MINIMUM_RESPONSE_THRESHOLD} responses are required before OTP
          pulse analytics can be viewed.
        </ComparisonPlaceholder>
      </Container>
    );
  }

  if (!dataSource?.questions?.length) {
    return (
      <Container>
        <EmptyState>
          <Icon name="chart bar outline" size="huge" />
          <h2>No Survey Data Available</h2>
          <p>
            There is currently no pulse survey data to display. Please check
            back once survey responses have been collected.
          </p>
        </EmptyState>
      </Container>
    );
  }

  const averageScore = selectedQuestion?.responses?.average;
  const totalResponses = selectedQuestion?.responses?.total ?? 0;

  return (
    <Container>
      <PrimaryTabs>
        <PrimaryTab
          type="button"
          onClick={() => setActivePrimaryTab("scores")}
          $active={activePrimaryTab === "scores"}
        >
          Score Distributions
        </PrimaryTab>
        <PrimaryTab
          type="button"
          onClick={() => setActivePrimaryTab("openEnded")}
          $active={activePrimaryTab === "openEnded"}
        >
          Open-Ended Responses
        </PrimaryTab>
      </PrimaryTabs>

      {activePrimaryTab === "scores" ? (
        <>
          {sharedRangeInfo?.ranges && (
            <LegendSection>
              <LegendBar>
                {DISTRIBUTION_SEGMENTS.map(({ key, label }) => {
                  const range = sharedRangeInfo.ranges?.[key];
                  if (!range) return null;
                  return (
                    <LegendItem key={`overall-${key}`}>
                      <LegendDot color={SEGMENT_META[key].color} />
                      <LegendValue>
                        {label} ({formatRange(range.start, range.end)})
                      </LegendValue>
                    </LegendItem>
                  );
                })}
              </LegendBar>
              {!sharedRangeInfo.consistent && (
                <LegendNote>
                  Ranges adjust slightly per question; see each row for exact
                  values.
                </LegendNote>
              )}
            </LegendSection>
          )}

          {selectedQuestion ? (
            <DetailPanel>
              <BackRow>
                <BackButton
                  type="button"
                  onClick={() => setSelectedQuestionId(null)}
                >
                  ← Back to all questions
                </BackButton>
              </BackRow>

              <DetailHeader>
                <DetailTitle>{selectedQuestion.text}</DetailTitle>
                <DetailSubtitle>{selectedQuestion.category}</DetailSubtitle>
                <DetailMeta>
                  <MetaTag>
                    Avg{" "}
                    {typeof averageScore === "number"
                      ? averageScore.toFixed(2)
                      : "—"}
                  </MetaTag>
                  <MetaTag>{totalResponses} responses</MetaTag>
                </DetailMeta>
              </DetailHeader>

              {parentCategoryOptions.length > 0 ? (
                <AnchorControls>
                  <AnchorLabel>Category</AnchorLabel>
                  <Select
                    value={selectedParentCategory || ""}
                    onChange={(event) =>
                      setSelectedParentCategory(event.target.value)
                    }
                  >
                    {parentCategoryOptions.map((option) => (
                      <option key={option.key} value={option.key}>
                        {option.name}
                      </option>
                    ))}
                  </Select>
                </AnchorControls>
              ) : null}

              {categoryDistributions.length ? (
                <CategoryTable>
                  <CategoryTableHeader>
                    <CategoryHeaderCell>Category</CategoryHeaderCell>
                    <CategoryHeaderCell>
                      Response Distribution
                    </CategoryHeaderCell>
                  </CategoryTableHeader>
                  <CategoryTableBody>
                    {categoryDistributions.map((category) => (
                      <CategoryTableRow key={category.name}>
                        <CategoryCell>
                          <CategoryName>{category.name}</CategoryName>
                          <CategoryMeta>
                            <span>
                              Avg{" "}
                              {typeof category.average === "number"
                                ? category.average.toFixed(2)
                                : "—"}
                            </span>
                            <span>{category.total} responses</span>
                          </CategoryMeta>
                        </CategoryCell>
                        <CategoryChartCell>
                          <QuestionDistributionChart data={category.segments} />
                        </CategoryChartCell>
                      </CategoryTableRow>
                    ))}
                  </CategoryTableBody>
                </CategoryTable>
              ) : (
                <ComparisonPlaceholder>
                  {parentCategoryOptions.length
                    ? "Not enough responses to show category distributions."
                    : "Category breakdowns are not configured for this survey."}
                </ComparisonPlaceholder>
              )}
            </DetailPanel>
          ) : quantitativeSummaries.length ? (
            <ScoresTable>
              <STableHeader>
                <SHeaderCell>Question</SHeaderCell>
                <SHeaderCell>Response Distribution</SHeaderCell>
              </STableHeader>
              <STableBody>
                {quantitativeSummaries.map((summary) => {
                  const { question, bands, ranges } = summary;
                  const average = question.responses?.average;
                  const responseCount = question.responses?.total ?? 0;

                  return (
                    <STableRow
                      key={question.id}
                      type="button"
                      onClick={() => setSelectedQuestionId(question.id)}
                    >
                      <QuestionCell>
                        <QuestionTitle>{question.text}</QuestionTitle>
                        <QuestionMeta>
                          <span>
                            Avg{" "}
                            {typeof average === "number"
                              ? average.toFixed(2)
                              : "—"}
                          </span>
                          <span>{responseCount} responses</span>
                        </QuestionMeta>
                      </QuestionCell>
                      <DistributionCell>
                        <DistributionWrapper>
                          <QuestionDistributionChart data={bands} />
                        </DistributionWrapper>
                        <DistributionFooter>
                          <ViewDetail>View by category →</ViewDetail>
                        </DistributionFooter>
                      </DistributionCell>
                    </STableRow>
                  );
                })}
              </STableBody>
            </ScoresTable>
          ) : (
            <ComparisonPlaceholder>
              No quantitative questions are available for this survey.
            </ComparisonPlaceholder>
          )}
        </>
      ) : (
        <OpenEndedContainer>
          <OpenEndedHeader>
            <OpenEndedTitle>
              {selectedOpenEndedQuestion
                ? selectedOpenEndedQuestion.text
                : "Open-Ended Responses"}
            </OpenEndedTitle>
            {selectedOpenEndedQuestion && (
              <OpenEndedMeta>
                {openEndedResponses.length} responses
              </OpenEndedMeta>
            )}
          </OpenEndedHeader>

          {openEndedQuestionOptions.length > 1 && (
            <OpenEndedSelectRow>
              <AnchorLabel>Switch question</AnchorLabel>
              <Select
                value={selectedOpenEndedId || ""}
                onChange={(event) => setSelectedOpenEndedId(event.target.value)}
              >
                {openEndedQuestionOptions.map((option) => (
                  <option key={option.question.id} value={option.question.id}>
                    {option.question.text}
                  </option>
                ))}
              </Select>
            </OpenEndedSelectRow>
          )}

          {selectedOpenEndedQuestion ? (
            openEndedResponses.length ? (
              <OpenEndedList>
                {openEndedResponses.map((item, index) => (
                  <OpenEndedCard
                    key={`${selectedOpenEndedQuestion.id}-${index}`}
                  >
                    <OpenEndedName>{item.name}</OpenEndedName>
                    <OpenEndedText>{item.response}</OpenEndedText>
                    {item.followUp && (
                      <FollowUpSection>
                        <FollowUpLabel>AI Follow-up</FollowUpLabel>
                        <FollowUpQuestion>
                          {item.followUp.question}
                        </FollowUpQuestion>
                        <FollowUpResponse>
                          {item.followUp.response}
                        </FollowUpResponse>
                      </FollowUpSection>
                    )}
                  </OpenEndedCard>
                ))}
              </OpenEndedList>
            ) : (
              <ComparisonPlaceholder>
                No responses yet for this question.
              </ComparisonPlaceholder>
            )
          ) : (
            <ComparisonPlaceholder>
              No open-ended questions have at least {MINIMUM_RESPONSE_THRESHOLD} responses.
            </ComparisonPlaceholder>
          )}
        </OpenEndedContainer>
      )}
    </Container>
  );
};

/* ─── Styled Components ─── */

const Container = styled.div`
  background: #ffffff;
  font-family: "Raleway", sans-serif;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 40px 48px;
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 40px;
  color: #8492a6;
  font-size: 14px;
  font-weight: 500;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const LoadingSpinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid #e2e8f0;
  border-top: 3px solid #2D70E2;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  margin-bottom: 16px;
`;

const PrimaryTabs = styled.div`
  display: flex;
  gap: 0;
  margin: 24px 0 28px;
  border-bottom: 1px solid #e2e8f0;
`;

const PrimaryTab = styled.button`
  background: none;
  border: none;
  border-bottom: 2px solid ${(props) => (props.$active ? "#2D70E2" : "transparent")};
  padding: 12px 20px;
  font-family: "Raleway", sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: ${(props) => (props.$active ? "#2D70E2" : "#8492a6")};
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
  margin-bottom: -1px;

  &:hover {
    color: #2D70E2;
  }
`;

const ScoresTable = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
`;

const STableHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1.8fr);
  background: #f8f9fc;
  padding: 14px 24px;
  border-bottom: 1px solid #e2e8f0;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    row-gap: 8px;
  }
`;

const SHeaderCell = styled.div`
  font-family: "Raleway", sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #8492a6;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const STableBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const STableRow = styled.button`
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1.8fr);
  gap: 24px;
  align-items: stretch;
  padding: 18px 24px;
  border: none;
  border-top: 1px solid #f0f3f8;
  background: #ffffff;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;

  &:first-child {
    border-top: none;
  }

  &:hover {
    background: #f8f9fc;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const QuestionCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DistributionCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
`;

const DistributionWrapper = styled.div`
  width: 100%;
`;

const DistributionFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 2px;

  @media (max-width: 900px) {
    justify-content: flex-start;
  }
`;

const ViewDetail = styled.span`
  font-family: "Raleway", sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #2D70E2;
  white-space: nowrap;
`;

const CategoryTable = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
`;

const CategoryTableHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1.8fr);
  background: #f8f9fc;
  padding: 14px 24px;
  border-bottom: 1px solid #e2e8f0;
  font-family: "Raleway", sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #8492a6;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    row-gap: 8px;
  }
`;

const CategoryHeaderCell = styled.div``;

const CategoryTableBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const CategoryTableRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1.8fr);
  gap: 24px;
  align-items: center;
  padding: 18px 24px;
  border-top: 1px solid #f0f3f8;

  &:first-child {
    border-top: none;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const CategoryCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CategoryChartCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const QuestionTitle = styled.h3`
  font-family: "Raleway", sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #1a1f36;
  margin: 0;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const QuestionMeta = styled.div`
  display: flex;
  gap: 12px;
  font-family: "Raleway", sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #8492a6;
  flex-wrap: wrap;
`;

const DetailPanel = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const BackRow = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  font-family: "Raleway", sans-serif;
  color: #2D70E2;
  font-size: 13px;
  font-weight: 700;
  padding: 4px 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    color: #1b4fa0;
  }
`;

const DetailHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DetailTitle = styled.h2`
  font-family: "Raleway", sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #1a1f36;
  margin: 0;
`;

const DetailSubtitle = styled.p`
  font-family: "Raleway", sans-serif;
  font-size: 14px;
  color: #8492a6;
  margin: 0;
`;

const DetailMeta = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 4px;
`;

const MetaTag = styled.span`
  font-family: "Raleway", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #4a5568;
  background: #f0f3f8;
  padding: 3px 10px;
  border-radius: 20px;
`;

const AnchorControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
`;

const AnchorLabel = styled.span`
  font-family: "Raleway", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #8492a6;
`;

const Select = styled.select`
  appearance: none;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 7px 12px;
  font-family: "Raleway", sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #1a1f36;
  cursor: pointer;
  transition: border-color 0.15s;

  &:focus {
    outline: none;
    border-color: #2D70E2;
  }
`;

const CategoryName = styled.h3`
  font-family: "Raleway", sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #1a1f36;
  margin: 0;
`;

const CategoryMeta = styled.div`
  display: flex;
  gap: 12px;
  font-family: "Raleway", sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #8492a6;
  flex-wrap: wrap;
`;

const LegendSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
  align-items: flex-end;
`;

const LegendBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  justify-content: flex-end;
`;

const LegendNote = styled.span`
  font-family: "Raleway", sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: #a0aec0;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LegendDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) => props.color};
  display: inline-block;
`;

const LegendValue = styled.span`
  font-family: "Raleway", sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #8492a6;
`;

const ComparisonPlaceholder = styled.div`
  background: #f8f9fc;
  border: 1px dashed #d7dce6;
  border-radius: 10px;
  padding: 36px;
  font-family: "Raleway", sans-serif;
  color: #8492a6;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
`;

const OpenEndedContainer = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const OpenEndedHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const OpenEndedTitle = styled.h2`
  font-family: "Raleway", sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #1a1f36;
  margin: 0;
`;

const OpenEndedMeta = styled.span`
  font-family: "Raleway", sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #8492a6;
`;

const OpenEndedSelectRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const OpenEndedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OpenEndedCard = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 18px 20px;
  background: #ffffff;
  transition: border-color 0.15s;

  &:hover {
    border-color: #cbd5e0;
  }
`;

const OpenEndedName = styled.div`
  font-family: "Raleway", sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #1a1f36;
  margin-bottom: 8px;
`;

const OpenEndedText = styled.div`
  font-family: "Raleway", sans-serif;
  font-size: 14px;
  color: #4a5568;
  line-height: 1.6;
  white-space: pre-line;
`;

const FollowUpSection = styled.div`
  margin-top: 14px;
  padding: 14px 16px;
  background: #f8f9fc;
  border-left: 3px solid #2D70E2;
  border-radius: 0 8px 8px 0;
`;

const FollowUpLabel = styled.div`
  font-family: "Raleway", sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #2D70E2;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 8px;
`;

const FollowUpQuestion = styled.div`
  font-family: "Raleway", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #1a1f36;
  margin-bottom: 6px;
  font-style: italic;
`;

const FollowUpResponse = styled.div`
  font-family: "Raleway", sans-serif;
  font-size: 14px;
  color: #4a5568;
  line-height: 1.6;
`;

const EmptyState = styled.div`
  padding: 80px 40px;
  text-align: center;
  color: #8492a6;
  border: 1px dashed #d7dce6;
  border-radius: 10px;
  background: #fafbfc;

  h2 {
    margin-top: 16px;
    font-family: "Raleway", sans-serif;
    font-size: 20px;
    color: #1a1f36;
    font-weight: 700;
  }

  p {
    font-family: "Raleway", sans-serif;
    font-size: 14px;
    line-height: 1.6;
    max-width: 400px;
    margin: 10px auto 0;
  }
`;

export default PulseAnalytics;
